import { reactive } from 'vue'
import { runCloudFunction } from '@/store/ParseUtils'
import { fetchBatchLoadable, fetchLibLoadable, LoadingStudentStats, resetLoadable } from '@/loadingUtils'
import { examModule } from '@/store/exam/module'
import { groupModule } from '@/store/group/module'
import { statsModule } from '@/store/stats/module'
import { studentModule } from '@/store/student/module'
import type { Study } from '@pocketprep/types'
import type {
    StudentStats,
    IQuizReducedObj,
    TFetchBatchQuizHistory,
    StudentAggStats,
    GroupAggStats,
} from '@/store/stats/types'
import { type TStudent } from '@/store/student/types'
import { organizationModule } from '@/store/organization/module'
import { assignmentModule } from '@/store/assignment/module'
import { stripHtmlTags } from '@/utils'

const resetStudentAggStats = () => {
    statsModule.state.studentAggStats = reactive({})
}

const resetStudentStats = () => {
    statsModule.state.studentStats = reactive({})
}

const resetGroupAggStats = () => {
    statsModule.state.groupAggStats = reactive({})
}

/**
 * Types for batching
 *
 * We are keeping these types localized to actions.ts since they are not being used anywhere else
 */

type TStudentIds = {
    studentId: string
    studentUserId: string
}

type TFetchBatchStudentStatsPayload = {
    students: TStudentIds[]
    compositeKey?: string
    examMetadataId?: string
}

type TFetchAggregateStudentStatsPayload = {
    students: TStudentIds[]
    compositeKey?: string
}

type TCurrentBatchStudentStats = {
    [studentId: string]: StudentStats
}

type TFetchBatchStudentQuizzes = {
    studentUserIds: string[]
    examGuid: string
    examMetadataId?: string
    suppressResponse?: boolean
}

type QuestionAnswerLib = { [serial: string]: Study.Cloud.TChoiceKey[] }

const createSubjectInsights = (
    questionSerialLib: { [serial: string]: Study.Class.QuestionJSON },
    incorrectSerials: string[],
    correctSerials: string[],
    initialSubjectInsights: { [subjectId: string]: Study.Class.SubjectInsight }
) => {
    const incrementInsight = (isCorrect: boolean) =>
        (acc: { [subjectId: string]: Study.Class.SubjectInsight }, serial: string) => {
            const question = questionSerialLib[serial]
            if (!question || question.isMockQuestion || question.isArchived) {
                return acc
            }

            const subject = question.subject as Study.Class.SubjectJSON

            if (!(subject.objectId in acc)) {
                acc[subject.objectId] = {
                    name: subject.name,
                    subjectId: subject.objectId,
                    correctAnswerCount: 0,
                    totalAnswers: 0,
                }
            }

            const subjectCounts = acc[subject.objectId]
            if (subjectCounts) {
                subjectCounts.correctAnswerCount += isCorrect ? 1 : 0
                subjectCounts.totalAnswers++
            }

            return acc
        }

    // first add correct subject serials to the subjet insights stats
    const correctSubjectInsights = correctSerials.reduce(incrementInsight(true), initialSubjectInsights)
    // then add the incorrect serials
    const subjectInsights = incorrectSerials.reduce(incrementInsight(false), correctSubjectInsights)

    return subjectInsights
}

const fetchBatchStudentQuizzes = async ({
    studentUserIds,
    examGuid,
    suppressResponse,
    examMetadataId,
}: TFetchBatchStudentQuizzes) => {
    const currentExam = examModule.getters.getCurrentGroupExam()

    return await runCloudFunction<TFetchBatchQuizHistory>('fetchBatchQuizHistory', {
        userIds: studentUserIds,
        examGuid,
        examMetadataId: examMetadataId || currentExam?.examMetadata?.objectId,
        suppressResponse: !!suppressResponse,
    })
}

type ChoiceLib = { [serial: string]: Study.Cloud.TChoiceKey[] }
const filterArchivedQuestions =
    (choiceLib: ChoiceLib, activeQuestions: ReturnType<typeof examModule.getters.getCurrentExamQuestionsBySerial>) =>
        Object.entries(choiceLib)
            .reduce<ChoiceLib>((answerAcc, [ serial, choices ]) => {
                if (serial in activeQuestions) {
                    answerAcc[serial] = choices
                }
                return answerAcc
            }, {})

const filterMockQuestions = (answers: QuestionAnswerLib, mockExamSerials: string[]) =>
    Object.entries(answers).reduce((acc, [ serial, choices ]) => {
        if (!mockExamSerials.includes(serial)) {
            acc[serial] = choices
        }
        return acc
    }, {} as QuestionAnswerLib)

const fetchAggregatedStudentStats = async ({
    students,
    compositeKey,
}: TFetchAggregateStudentStatsPayload) => {
    if (!students || students.length === 0 || !compositeKey) {
        return
    }

    const studentIds = students.map(s => s.studentId)
    const studentUserIds = students.map(s => s.studentUserId)

    return fetchBatchLoadable(statsModule, 'studentAggStats', studentIds, async (filteredStudentIds) => {
        const {
            studentIdUserIdLib,
        } = students.reduce((acc, s) => {
            acc.studentIdUserIdLib[s.studentId] = s.studentUserId
            return acc
        }, { studentIdUserIdLib: {} } as { studentIdUserIdLib: { [studentId: string]: string } })
        const examGuid = compositeKey.split('/')[0]?.toUpperCase()
        if (!examGuid) {
            throw new Error('fetchAggregatedStudentStats: invalid compositeKey')
        }
        const currentOrganization = organizationModule.getters.getCurrentOrganization()
        if (!currentOrganization) {
            throw new Error('fetchAggregatedStudentStats: Could not find currentOrganization')
        }

        const currentExamMetadataId = examModule.getters.getCurrentGroupExam()?.examMetadata.objectId

        if (!currentExamMetadataId) {
            throw new Error('fetchAggregatedStudentStats: Could not find currentExamMetadata')
        }

        await Promise.all([
            studentModule.actions.fetchOrgStudentUEMForStudents(studentIds),
            examModule.actions.fetchQuestionsByExam({
                compositeKey,
                organizationId: currentOrganization.objectId,
                examMetadataId: currentExamMetadataId,
            }),
        ])

        let studentUEMs = studentModule.getters.getCurrentOrgStudentUEM()
        const examQuestionsBySerial = examModule.getters.getCurrentExamQuestionsBySerial()
        const mockExamSerials = Object.keys(examQuestionsBySerial).filter(serial => {
            const question = examQuestionsBySerial[serial]
            return question?.isMockQuestion
        })
        const totalQuestions = Object.keys(examQuestionsBySerial).length - mockExamSerials.length

        // if any UEMs are missing aggregate stats, trigger server request that generates them
        const uemMissingAggStats = Object.values(studentUEMs)
            .flat()
            .filter(uem => uem.examGuid === examGuid
                && !uem.aggregateStats?.correctQuestionAnswerLib
                && !uem.aggregateStats?.incorrectQuestionAnswerLib
                && studentUserIds.includes(uem.user.objectId)
            )
        if (uemMissingAggStats.length) {
            const userIds = uemMissingAggStats.map(uem => uem.user.objectId)
            const batchSize = 20
            const batchPromises: Promise<unknown>[] = []
            for (let i = 0; i < userIds.length; i += batchSize) {
                const userIdBatch = userIds.slice(i, i + batchSize)
                // Batch fetch
                batchPromises.push(fetchBatchStudentQuizzes({
                    studentUserIds: userIdBatch,
                    examGuid,
                    suppressResponse: true,
                }))
            }

            await Promise.all(batchPromises)

            currentOrganization && resetLoadable(studentModule.state.currentOrgStudentUEM[currentOrganization.objectId])
            const studentIdsMissingAggStats = studentIds.filter(studentId => {
                const userIdForStudentId = studentIdUserIdLib[studentId]
                return userIdForStudentId && userIds.includes(userIdForStudentId)
            })
            await studentModule.actions.fetchOrgStudentUEMForStudents(studentIdsMissingAggStats, { forceFetch: true })
            studentUEMs = studentModule.getters.getCurrentOrgStudentUEM()
        }

        const studentStats = filteredStudentIds.reduce((acc, studentId) => {
            const userId = studentIdUserIdLib[studentId]
            if (!userId) {
                return acc
            }

            const uems = studentUEMs[userId] || []
            // Do a search based on currentExamMetadataId instead of examGuid since we could have multiple versions
            const uem = uems.find(u => u.examMetadata?.objectId === currentExamMetadataId)

            const correctAnswers =
                filterArchivedQuestions(uem?.aggregateStats?.correctQuestionAnswerLib || {}, examQuestionsBySerial)
            const incorrectAnswers =
                filterArchivedQuestions(uem?.aggregateStats?.incorrectQuestionAnswerLib || {}, examQuestionsBySerial)
            const incorrectWithoutMock = filterMockQuestions(incorrectAnswers, mockExamSerials)
            const correctWithoutMock = filterMockQuestions(correctAnswers, mockExamSerials)
            const incorrectWithoutMockSerials = Object.keys(incorrectWithoutMock)
            const correctWithoutMockSerials = Object.keys(correctWithoutMock)
            const subjectInsights = createSubjectInsights(
                examQuestionsBySerial,
                incorrectWithoutMockSerials,
                correctWithoutMockSerials,
                {}
            )
            const flaggedQuestionSerials = uem?.flaggedQuestions || []

            acc[studentId] = {
                correctAnswerCount: correctWithoutMockSerials.length,
                totalAnswers: correctWithoutMockSerials.length + incorrectWithoutMockSerials.length,
                totalQuestions,
                subjectInsights: Object.values(subjectInsights).map(si => ({
                    name: si.name,
                    correctCount: si.correctAnswerCount,
                    answeredCount: si.totalAnswers,
                })),
                correctAnswerSerials: correctWithoutMockSerials,
                incorrectAnswerSerials: incorrectWithoutMockSerials,
                flaggedQuestionSerials,
                questionAnswerLib: { ...incorrectAnswers, ...correctAnswers },
            } as StudentAggStats

            return acc
        }, {} as { [studentId: string]: StudentAggStats })

        return studentStats
    })
}

const fetchBatchStudentStats = async ({
    students,
    compositeKey,
    examMetadataId,
}: TFetchBatchStudentStatsPayload) => {
    if (!students || students.length === 0 || !compositeKey) {
        return LoadingStudentStats
    }

    const examGuid = compositeKey.split('/')[0]?.toUpperCase()
    if (!examGuid) {
        throw new Error('fetchBatchStudentStats: invalid compositeKey')
    }
    const currentOrganization = organizationModule.getters.getCurrentOrganization()
    if (!currentOrganization) {
        throw new Error('fetchBatchStudentStats: Could not find currentOrganization')
    }

    const studentIds = students.map(student => student.studentId)
    const allExamMetadata = examModule.getters.getExamMetadata()
    const currentExamMetadata = examMetadataId
        ? allExamMetadata[examMetadataId]
        : examModule.getters.getCurrentGroupExam()?.examMetadata

    if (!currentExamMetadata) {
        throw new Error('fetchBatchStudentStats: Could not find currentExamMetadata')
    }

    await Promise.all([
        studentModule.actions.fetchCurrentOrgStudentUsers(),
        studentModule.actions.fetchOrgStudentUEMForStudents(studentIds),
        examModule.actions.fetchQuestionsByExam({
            compositeKey,
            organizationId: currentOrganization.objectId,
            examMetadataId: currentExamMetadata.objectId,
        }),
        assignmentModule.actions.fetchAssignments(),
    ])

    const studentUsers = studentModule.getters.getCurrentOrgStudentUsers()
    const studentUEMs = studentModule.getters.getCurrentOrgStudentUEM()
    const assignments = assignmentModule.getters.getCurrentOrgAssignments()

    const currentBatchStudentStats: TCurrentBatchStudentStats = {}

    return fetchBatchLoadable(statsModule, 'studentStats', studentIds, async (filteredStudentIds) => {
        const filteredStudents = students.filter(student => filteredStudentIds.includes(student.studentId))
        const filteredStudentUserIds = filteredStudents.map(student => student.studentUserId)

        const currentBatchStudentQuizzes = await fetchBatchStudentQuizzes({
            studentUserIds: filteredStudentUserIds,
            examGuid,
            examMetadataId: currentExamMetadata.objectId,
        }) || {}

        filteredStudents.forEach(student => {
            const studentUserId = student.studentUserId
            const currentStudentUser = studentUsers[studentUserId]
            const currentStudentUEMs = studentUEMs[studentUserId] || []
            const currentStudentUEM = currentStudentUEMs.find(uem => uem.examGuid === examGuid)
            const examQuestionsBySerial = examModule.getters.getCurrentExamQuestionsBySerial()

            const quizzes = currentBatchStudentQuizzes[studentUserId] || []
            studentModule.state.currentOrgStudentQuizzes = quizzes

            // reduce answer records
            const {
                answeredQuestions,
                answerLib,
                qotdActivity,
                dayScoresLib,
                quizScores,
                secondsStudied,
                allActivity,
            } = quizzes.reduce<IQuizReducedObj>((quizAcc, quiz) => {
                type TQuizMode = -1 | 0 | 1 | 2 | 3 | 4 | 5 | 6 | 10 | 11
                const mode = quiz.mode !== null
                    ? quiz.mode as TQuizMode
                    : 10 // default to 10 (custom) if no mode set
                const answers = quiz.answers
                const correctCount = quiz.correctCount
                const incorrectCount = quiz.incorrectCount
                const durationSeconds = quiz.durationSeconds
                const submittedDate = new Date(quiz.createdAt)
                const date = submittedDate.toLocaleDateString()
                const quizId = quiz.objectId
                const levelUpQuizProgress = quiz.levelUpProgress
                const quizCreatedAt = quiz.createdAt
                // Ignore quizzes that have no answers
                if (answers.length === 0) {
                    return quizAcc
                }

                // mode === -1 is QotD
                if (mode === -1) {
                    quizAcc.qotdActivity.push({
                        date,
                        isCorrectAnswer: correctCount > 0,
                    })
                    const newQotdActivity = {
                        score: correctCount > 0 ? 1 as const : 0 as const,
                        date,
                        mode,
                        answers,
                        duration: durationSeconds,
                        quizId,
                        quizCreatedAt,
                    }
                    quizAcc.allActivity.push(newQotdActivity)
                } else {
                    const totalQuizQuestions = correctCount + incorrectCount
                    const score = Math.round(((correctCount || 0) / (totalQuizQuestions || Infinity)) * 100)
                    const assignment = quiz.prebuiltQuiz && assignments?.[quiz.prebuiltQuiz?.objectId]
                    const quizActivity = {
                        score,
                        date,
                        mode,
                        answers,
                        duration: durationSeconds,
                        quizId,
                        assignment,
                        levelUpQuizProgress,
                        quizCreatedAt,
                    }
                    quizAcc.allActivity.push(quizActivity)

                    // Mock Exams are not included in quizScores
                    if (mode !== 5) {
                        quizAcc.quizScores.push(quizActivity)
                        if (!(date in quizAcc.dayScoresLib)) {
                            quizAcc.dayScoresLib[date] = {
                                date,
                                score,
                            }
                        } else {
                            const quizCount = quizAcc.quizScores.filter(q => q.date === date).length
                            const dayScores = quizAcc.dayScoresLib[date]
                            if (dayScores) {
                                dayScores.score = Math.round(
                                    (dayScores.score * (quizCount - 1) + score) / quizCount
                                )
                            }
                        }
                    }
                }

                // Do not include Mock Exam records in total time studied
                // or in the rolling up of answers from Quizzes
                if (mode !== 5) {
                    quizAcc.secondsStudied += durationSeconds

                    answers.forEach(answer => {
                        const isCorrectAnswer = answer.isCorrect
                        const serial = answer.questionSerial
                        const question = examQuestionsBySerial[serial]

                        if (!question) {
                            return
                        }

                        const existingAnsweredQuestion = quizAcc.answeredQuestions.find(answeredQuestion =>
                            answeredQuestion.question.serial === answer.questionSerial)
                        if (!existingAnsweredQuestion) {
                            quizAcc.answeredQuestions.push({
                                question,
                                answer,
                            })
                        } else if (answer.isCorrect && !existingAnsweredQuestion.answer.isCorrect) {
                            existingAnsweredQuestion.answer = answer
                        }

                        const subject = question.subject as Study.Class.SubjectJSON
                        if (!(subject.name in quizAcc.subjectInsightsLib)) {
                            quizAcc.subjectInsightsLib[subject.name] = {
                                name: subject.name,
                                correctCount: 0,
                                answeredCount: 0,
                            }
                        }

                        const existingAnswer = quizAcc.answerLib[serial]
                        if (!existingAnswer) {
                            quizAcc.answerLib[serial] = {
                                ...answer,
                                answeredAt: submittedDate,
                                subject: subject.name,
                            }
                            // Increase total counts if we haven't already counted this serial
                            const subjectInsight = quizAcc.subjectInsightsLib[subject.name]
                            if (subjectInsight) {
                                subjectInsight.answeredCount++
                            }
                            quizAcc.totalAnswers++
                        } else {
                            quizAcc.answerLib[serial] = {
                                ...existingAnswer,
                                // Use most recent answered date
                                answeredAt: submittedDate > existingAnswer.answeredAt
                                    ? submittedDate
                                    : existingAnswer.answeredAt,
                            }
                        }

                        // Increase correct counts if this serial has not been answered correctly before
                        if (isCorrectAnswer && !existingAnswer?.isCorrect) {
                            quizAcc.answerLib[serial].isCorrect = isCorrectAnswer
                            const subjectInsight = quizAcc.subjectInsightsLib[subject.name]
                            if (subjectInsight) {
                                subjectInsight.correctCount++
                            }
                            quizAcc.correctAnswerCount++
                        }
                    })
                }
                return quizAcc
            }, {
                answeredQuestions: [],
                answerLib: {},
                subjectInsightsLib: {},
                qotdActivity: [],
                correctAnswerCount: 0,
                totalAnswers: 0,
                secondsStudied: 0,
                dayScoresLib: {},
                quizScores: [],
                allActivity: [],
            })

            // prepare flagged questions list
            const flaggedQuestionSerials = currentStudentUEM?.flaggedQuestions || []
            const flaggedQuestions = flaggedQuestionSerials.reduce((acc, serial) => {
                const q = examQuestionsBySerial[serial]
                if (q) { // Pull mock questions up but filter them out higher up
                    const s = q.subject as Study.Class.SubjectJSON
                    const scenarioPartLabel = examModule.getters.getScenarioPartLabel(q)
                    const strippedQuestionPrompt = stripHtmlTags(q.prompt)
                    acc.push({
                        serial,
                        subject: s.name,
                        prompt: strippedQuestionPrompt,
                        answeredCorrectly: answerLib[q.serial]?.isCorrect || false,
                        unread: true, // TODO: I have no idea where to store this yet so we're defaulting to true.
                        isMockQuestion: q.isMockQuestion,
                        scenarioPartLabel,
                    })
                }
                return acc
            }, [] as {
                serial: string
                subject: string
                prompt: string
                answeredCorrectly: boolean
                unread: boolean
                isMockQuestion: boolean
                scenarioPartLabel: string
            }[])

            // prepare user info
            const scheduledExamDate = currentStudentUEM?.scheduledExamDate
                && new Date(currentStudentUEM.scheduledExamDate.iso).toLocaleDateString()
            const userInfo = {
                firstName: currentStudentUser?.firstName,
                lastName: currentStudentUser?.lastName,
                scheduledExamDate,
            }

            const dayScores = Object.values(dayScoresLib)
            const datesActive = [ ...new Set(allActivity.map(a => a.date)
                .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())) ]

            currentBatchStudentStats[student.studentId] = {
                answeredQuestions,
                userInfo,
                flaggedQuestions,
                qotdActivity,
                secondsStudied,
                dayScores,
                quizScores,
                allActivity,
                datesActive,
            }
        })
        return currentBatchStudentStats
    })
}

const fetchCurrentStudentStats = async () => {
    const currentStudent = await studentModule.actions.fetchCurrentStudent()
    const currentStudentExam = await examModule.actions.fetchCurrentStudentCurrentExam()

    if (currentStudent && currentStudent.user && currentStudentExam) {
        const studentId = currentStudent.objectId
        const studentUserId = currentStudent.user.objectId

        const compositeKey = (currentStudentExam
            && 'compositeKey' in currentStudentExam.examMetadata
            && currentStudentExam.examMetadata.compositeKey) || undefined

        const [ aggStats, studentStats ] = await Promise.all([
            fetchAggregatedStudentStats({
                students: [{ studentId, studentUserId }],
                compositeKey,
            }),
            fetchBatchStudentStats({
                students: [{ studentId, studentUserId }],
                compositeKey,
            }),
        ])

        return {
            ...studentStats,
            ...aggStats,
        }
    }
}

const fetchCurrentGroupAggStats = async () => {
    const [ students, currentGroup, orgExams, allExamMetadata ] = await Promise.all([
        studentModule.actions.fetchCurrentGroupStudents(),
        groupModule.actions.fetchCurrentGroup(),
        examModule.actions.fetchCurrentOrgExams(),
        examModule.actions.fetchExamMetadata(),
    ])

    const orgExam = currentGroup && orgExams[currentGroup.orgExam.objectId]
    if (!currentGroup || !orgExam) {
        return
    }

    const groupExamMetadata = allExamMetadata[orgExam.examMetadata.objectId]
    if (!groupExamMetadata) {
        throw new Error('fetchCurrentGroupAggStats: Could not find groupExamMetadata')
    }
    const currentOrganization = organizationModule.getters.getCurrentOrganization()
    if (!currentOrganization) {
        throw new Error('fetchCurrentGroupAggStats: Could not find currentOrganization')
    }
    const [ questions ] = await Promise.all([
        examModule.actions.fetchQuestionsByExam({
            compositeKey: groupExamMetadata.compositeKey,
            organizationId: currentOrganization.objectId,
            examMetadataId: groupExamMetadata.objectId,
        }),
    ])

    const quantiles = [
        '0-10',
        '10-20',
        '20-30',
        '30-40',
        '40-50',
        '50-60',
        '60-70',
        '70-75',
        '75-80',
        '80-90',
        '90-100',
    ] as const
    const zeroedQuantiles = quantiles.reduce((zeroed, quantile) => ({
        ...zeroed,
        [quantile]: 0,
    }), {} as { [key in (typeof quantiles)[number]]: 0 })

    await examModule.actions.fetchSubjectsWithLevels()

    return fetchLibLoadable(statsModule, 'groupAggStats', currentGroup.objectId, async (): Promise<GroupAggStats> => {
        const studentsToFetch = students.reduce((acc, student) => {
            if (student && student.user) {
                acc.push({
                    studentId: student.objectId,
                    studentUserId: student.user.objectId,
                })
            }
            return acc
        }, [] as { studentId: string; studentUserId: string }[])
        await fetchAggregatedStudentStats({
            students: studentsToFetch,
            compositeKey: groupExamMetadata.compositeKey,
        })
        const questionSerialLib = questions.reduce((acc, q) => {
            acc[q.serial] = q
            return acc
        }, {} as { [serial: string]: Study.Class.QuestionJSON })

        const studentStats = statsModule.getters.getStudentAggStats()
        // Filter out any student that doesn't have a user (so no stats)
        const currentGroupStudents = studentModule.getters.getCurrentGroupStudents().filter(cgs => cgs.user)
        const studentUsers = studentModule.getters.getCurrentOrgStudentUsers()
        const groupStudentStats = currentGroupStudents.reduce((acc, orgStudent) => {
            if (!orgStudent || !orgStudent.user) {
                return acc
            }

            const statsValue = studentStats[orgStudent.objectId]
            const studentUser = studentUsers[orgStudent.user.objectId]
            const name = orgStudent.displayFirstName || orgStudent.displayLastName
                ? [ orgStudent.displayFirstName, orgStudent.displayLastName ].join(' ')
                : studentUser
                    ? studentUser.firstName || studentUser.lastName
                        ? [ studentUser.firstName, studentUser.lastName ].join(' ')
                        : studentUser.username
                    : ''

            if (statsValue && !(statsValue instanceof Promise)) {
                acc.push({
                    ...statsValue,
                    name,
                    orgStudent,
                })
            }
            return acc
        }, [] as (StudentAggStats & { name: string; orgStudent: TStudent })[])

        // if group stats are missing, just return initial state
        if (groupStudentStats?.length !== currentGroupStudents.length) {
            const currentExam = examModule.getters.getCurrentGroupExam()
            const examMetadataId = currentExam?.examMetadata.objectId
            const examMetadata = examMetadataId && allExamMetadata[examMetadataId]
            const subjectInsights = examMetadata && Object.keys(examMetadata.knowledgeAreas).map(ka => ({
                subjectName: ka,
                distribution: {
                    ...zeroedQuantiles,
                    countBelow50: 0,
                    count50to75: 0,
                    countAbove75: 0,
                },
                scores: [],
            })) || []

            return {
                isLoading: true,
                overallScorePct: 0,
                answeredCount: 0,
                totalCount: 0,
                subjectInsights,
                questionStats: [],
            }
        }

        type TGroupStats = Omit<GroupAggStats, 'flaggedQuestions' | 'questionStats'> & {
            questionStatsLib: {
                [serial: string]: GroupAggStats['questionStats'][0]
            }
        }
        const groupStats = groupStudentStats.reduce<TGroupStats>((acc, stats, index) => {
            const scorePct = Math.round(
                (stats.correctAnswerCount / (stats.totalAnswers || Infinity)) * 100
            )

            // Calculate question specific stats
            const incrementQuestionStats = (lib: TGroupStats['questionStatsLib'], isCorrect: boolean | null) =>
                (serial: string) => {
                    if (!(serial in lib)) {
                        const q = questionSerialLib[serial]
                        if (!q) {
                            return
                        }

                        const scenarioPartLabel = examModule.getters.getScenarioPartLabel(q)
                        const strippedQuestionPrompt = stripHtmlTags(q.prompt)

                        lib[serial] = {
                            serial,
                            isMockQuestion: q.isMockQuestion,
                            correctAnswerCount: 0,
                            totalAnswers: 0,
                            prompt: strippedQuestionPrompt,
                            subject: (q.subject as Study.Class.SubjectJSON).name,
                            numStudentsFlagged: 0,
                            answers: [],
                            scenarioPartLabel,
                        }
                    }

                    const answers = stats.questionAnswerLib[serial]
                    if (answers?.length && isCorrect !== null) {
                        const question = lib[serial]
                        if (question) {
                            question.answers.push({
                                isCorrect,
                                selectedChoices: answers,
                            })
                            question.totalAnswers++
                            question.correctAnswerCount += isCorrect ? 1 : 0
                        }
                    }

                    if (isCorrect === null) {
                        const isFlagged = stats.flaggedQuestionSerials.includes(serial)
                        const question = lib[serial]
                        if (question) {
                            question.numStudentsFlagged += isFlagged ? 1 : 0
                        }
                    }
                }

            stats.correctAnswerSerials.forEach(incrementQuestionStats(acc.questionStatsLib, true))
            stats.incorrectAnswerSerials.forEach(incrementQuestionStats(acc.questionStatsLib, false))
            stats.flaggedQuestionSerials.forEach(incrementQuestionStats(acc.questionStatsLib, null))

            // Rolling averages for each group stat
            if (stats.totalAnswers > 0) {
                acc.overallScorePct = ((acc.overallScorePct * index) + scorePct) / (index + 1)
            }
            acc.totalCount = ((acc.totalCount * index) + stats.totalQuestions) / (index + 1)
            acc.answeredCount = ((acc.answeredCount * index) + stats.totalAnswers) / (index + 1)
            stats.subjectInsights.forEach(studentSubjectStat => {
                const studentScore = Math.round(
                    (studentSubjectStat.correctCount || 0)
                    / (studentSubjectStat.answeredCount || Infinity) * 100
                )

                // Map score to quantile: 0 -> '0-10', 15 -> '10-20', 77 -> '75-80', 100 -> '90-100' etc
                const distributionSection = quantiles
                    .find(quantile => studentScore < Number(quantile.split('-')[1])) || '90-100'

                const existingGroupStat = acc.subjectInsights
                    .find(groupStat => groupStat.subjectName === studentSubjectStat.name)
                if (existingGroupStat) {
                    existingGroupStat.distribution[distributionSection]++
                    if (studentScore < 50) {
                        existingGroupStat.distribution.countBelow50++
                    } else if (studentScore <= 75) {
                        existingGroupStat.distribution.count50to75++
                    } else {
                        existingGroupStat.distribution.countAbove75++
                    }
                    existingGroupStat.scores.push({
                        name: stats.name,
                        scorePct: studentScore,
                    })
                } else {
                    const initialDistribution = {
                        ...zeroedQuantiles,
                        countBelow50: 0,
                        count50to75: 0,
                        countAbove75: 0,
                    }
                    initialDistribution[distributionSection]++
                    if (studentScore < 50) {
                        initialDistribution.countBelow50++
                    } else if (studentScore <= 75) {
                        initialDistribution.count50to75++
                    } else {
                        initialDistribution.countAbove75++
                    }

                    acc.subjectInsights.push({
                        subjectName: studentSubjectStat.name,
                        distribution: initialDistribution,
                        scores: [{
                            name: stats.name,
                            scorePct: studentScore,
                        }],
                    })
                }
            })

            return acc
        }, {
            overallScorePct: 0,
            answeredCount: 0,
            totalCount: 0,
            subjectInsights: [],
            questionStatsLib: {},
        } as TGroupStats)

        return {
            isLoading: false,
            overallScorePct: Math.round(groupStats.overallScorePct),
            answeredCount: Math.round(groupStats.answeredCount),
            totalCount: Math.round(groupStats.totalCount),
            subjectInsights: groupStats.subjectInsights,
            questionStats: Object.values(groupStats.questionStatsLib),
        }
    })
}

export default {
    resetStudentAggStats,
    resetStudentStats,
    resetGroupAggStats,
    fetchCurrentStudentStats,
    fetchCurrentGroupAggStats,
    fetchBatchStudentStats,
    createSubjectInsights,
    fetchAggregatedStudentStats,
}
